import { useState } from "react";
import {
  FederationMessageDirection,
  FederationMessageType,
  useFederationMessagesQuery,
  useSendFederationMessageMutation,
} from "../__generated__/graphql";

export interface FederationMessageThreadProps {
  federationLinkId: string;
  stepRunId?: string;
}

/**
 * Clarification / status thread scoped to a federation link (and optionally
 * a single step_run). Messages load through the query and refetch after a
 * send; outgoing messages hit the sendFederationMessage mutation. There is no
 * push channel: the onFederationMessage subscription was removed because it
 * had no resolver authorizing the partner link or work item.
 */
export function FederationMessageThread({
  federationLinkId,
  stepRunId,
}: FederationMessageThreadProps) {
  const [draft, setDraft] = useState("");
  const { data, loading, refetch } = useFederationMessagesQuery({
    variables: { federationLinkId, page: { first: 50 } },
  });
  const [send, { loading: sending }] = useSendFederationMessageMutation();

  const messages = data?.federationLink?.messages.edges ?? [];

  return (
    <section aria-label="Federation messages">
      <h2>Thread</h2>
      {loading && <p>Loading messages…</p>}
      <div className="federation-thread" role="log" aria-live="polite">
        {messages.map(({ node }) => (
          <article
            key={node.id}
            className={`msg ${node.direction === FederationMessageDirection.Inbound ? "inbound" : "outbound"}`}
          >
            <header>
              <strong>{node.author?.fullName ?? node.direction}</strong>
              <small> · {node.messageType}</small>
            </header>
            <p>{node.body}</p>
          </article>
        ))}
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const body = draft.trim();
          if (!body || !stepRunId) return;
          await send({
            variables: {
              input: {
                federationLinkId,
                workItemId: stepRunId,
                relatedStepRunId: stepRunId,
                body,
                messageType: FederationMessageType.ClarificationRequest,
              },
            },
          });
          setDraft("");
          void refetch();
        }}
      >
        <label>
          Ask a clarification
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. margin unclear, need retake"
          />
        </label>
        <button type="submit" disabled={sending || !draft.trim()}>
          {sending ? "Sending…" : "Send"}
        </button>
      </form>
    </section>
  );
}
